import React, { useState, useEffect } from "react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Building2, Loader2, Crown, Ban, CheckCircle2, UserPlus, X, ShieldAlert, Database, Download, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [ownersTarget, setOwnersTarget] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedCand, setSelectedCand] = useState("");

  const [dataTarget, setDataTarget] = useState(null);
  const [backups, setBackups] = useState([]);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const restoreRef = React.useRef(null);

  const apiBase = API.replace(/\/api$/, "");

  const token = () => localStorage.getItem("token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/companies`, { headers: authHeaders() });
      setCompanies(await res.json());
    } catch (e) {
      toast.error("Could not load companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const doSuspend = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/companies/${suspendTarget.id}/suspend`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ reason: suspendReason }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success(`${suspendTarget.name} suspended`);
      setSuspendTarget(null); setSuspendReason(""); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doReactivate = async (c) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/companies/${c.id}/reactivate`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success(`${c.name} reactivated`); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openOwners = async (c) => {
    setOwnersTarget(c); setSelectedCand("");
    try {
      const res = await fetch(`${API}/admin/companies/${c.id}/candidates`, { headers: authHeaders() });
      setCandidates(await res.json());
    } catch (e) { setCandidates([]); }
  };

  const addOwner = async () => {
    if (!selectedCand) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/companies/${ownersTarget.id}/owners`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ user_id: selectedCand }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success("Owner added");
      await load();
      const updated = (await (await fetch(`${API}/admin/companies`, { headers: authHeaders() })).json()).find(x => x.id === ownersTarget.id);
      setOwnersTarget(updated); openOwners(updated);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const removeOwner = async (userId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/companies/${ownersTarget.id}/owners/${userId}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success("Owner removed");
      const updated = (await (await fetch(`${API}/admin/companies`, { headers: authHeaders() })).json()).find(x => x.id === ownersTarget.id);
      setOwnersTarget(updated); openOwners(updated); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openData = async (c) => {
    setDataTarget(c); setPurgeConfirm(""); setBackups([]);
    await loadBackups(c.id);
  };

  const loadBackups = async (id) => {
    try {
      const res = await fetch(`${API}/admin/companies/${id}/backups`, { headers: authHeaders() });
      setBackups(await res.json());
    } catch (e) { setBackups([]); }
  };

  const startBackup = async () => {
    setBackupBusy(true);
    try {
      const res = await fetch(`${API}/admin/companies/${dataTarget.id}/backup`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success("Backup started…");
      // poll until the newest backup is ready (max ~15s)
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 1500));
        const r2 = await fetch(`${API}/admin/companies/${dataTarget.id}/backups`, { headers: authHeaders() });
        const list = await r2.json();
        setBackups(list);
        if (list[0] && list[0].status !== "running") break;
      }
    } catch (e) { toast.error(e.message); } finally { setBackupBusy(false); }
  };

  const downloadBackup = (b) => {
    const url = `${apiBase}/api/admin/backups/${b.id}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${(dataTarget?.name || "company").replace(/\s+/g, "_")}_backup.zip`;
        a.click(); URL.revokeObjectURL(a.href);
      }).catch(() => toast.error("Download failed"));
  };

  const doPurge = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/companies/${dataTarget.id}/purge`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ confirm_name: purgeConfirm }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const d = await res.json();
      toast.success(`${dataTarget.name} purged`);
      setDataTarget(null); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doRestore = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setRestoring(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${API}/admin/restore`, { method: "POST", headers: { Authorization: `Bearer ${token()}` }, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Restore failed");
      const d = await res.json();
      toast.success(`Restored as "${d.new_name}"`);
      load();
    } catch (er) { toast.error(er.message); } finally { setRestoring(false); if (restoreRef.current) restoreRef.current.value = ""; }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-companies-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><Building2 className="w-6 h-6 text-amber-400" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Companies</h1>
          <p className="text-slate-500 text-sm">Manage company owners and account status (operator only).</p>
        </div>
        <div className="ml-auto">
          <input ref={restoreRef} type="file" accept=".zip" className="hidden" onChange={doRestore} data-testid="restore-input" />
          <Button variant="outline" size="sm" onClick={() => restoreRef.current?.click()} disabled={restoring} data-testid="restore-btn">
            {restoring ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            Restore from backup
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {companies.map((c) => (
          <Card key={c.id} className={`border ${c.status !== "active" ? "border-red-200 bg-red-50/40" : "border-slate-200"}`} data-testid={`company-card-${c.id}`}>
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{c.name}</span>
                  {c.status === "active" ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                  ) : (
                    <Badge variant="destructive" data-testid={`company-status-${c.id}`}>{c.status === "purged" ? "Purged" : "Suspended"}</Badge>
                  )}
                </div>
                <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-amber-500" />{c.owners.map(o => o.username).join(", ") || "—"}</span>
                  <span>{c.member_count} members</span>
                  <span>{c.site_count} sites</span>
                </div>
                {c.status === "suspended" && c.suspend_reason && (
                  <div className="text-xs text-red-600 mt-1">Reason: {c.suspend_reason}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => openOwners(c)} data-testid={`manage-owners-${c.id}`}>
                  <Crown className="w-4 h-4 mr-1.5" /> Owners
                </Button>
                <Button variant="outline" size="sm" onClick={() => openData(c)} data-testid={`data-${c.id}`}>
                  <Database className="w-4 h-4 mr-1.5" /> Data
                </Button>
                {c.status === "active" ? (
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setSuspendTarget(c); setSuspendReason(""); }} data-testid={`suspend-${c.id}`}>
                    <Ban className="w-4 h-4 mr-1.5" /> Suspend
                  </Button>
                ) : c.status === "suspended" ? (
                  <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" disabled={busy} onClick={() => doReactivate(c)} data-testid={`reactivate-${c.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Reactivate
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Suspend dialog */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><ShieldAlert className="w-5 h-5" /> Suspend {suspendTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-600">All users of this company will be blocked from logging in, and their client/contractor links will stop working. Data is preserved. You can reactivate any time.</p>
            <div>
              <Label htmlFor="suspend-reason">Reason (optional)</Label>
              <Input id="suspend-reason" data-testid="suspend-reason-input" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder="e.g. Non-payment" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendTarget(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={doSuspend} data-testid="confirm-suspend-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Suspend company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owners dialog */}
      <Dialog open={!!ownersTarget} onOpenChange={(o) => !o && setOwnersTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> Owners — {ownersTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-2">
              {ownersTarget?.owners?.map((o) => (
                <div key={o.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`owner-row-${o.id}`}>
                  <div>
                    <div className="text-sm font-medium text-slate-800">{o.username}</div>
                    <div className="text-xs text-slate-500">{o.email}</div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" disabled={busy || ownersTarget.owners.length <= 1} title={ownersTarget?.owners?.length <= 1 ? "Cannot remove the only owner" : "Remove owner"} onClick={() => removeOwner(o.id)} data-testid={`remove-owner-${o.id}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="border-t pt-3">
              <Label className="text-sm">Add an owner</Label>
              <div className="flex gap-2 mt-1">
                <Select value={selectedCand} onValueChange={setSelectedCand}>
                  <SelectTrigger data-testid="owner-candidate-select"><SelectValue placeholder={candidates.length ? "Select a team member" : "No eligible members"} /></SelectTrigger>
                  <SelectContent>
                    {candidates.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.username} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold shrink-0" disabled={busy || !selectedCand} onClick={addOwner} data-testid="add-owner-btn">
                  <UserPlus className="w-4 h-4 mr-1.5" /> Add
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Promotes the selected member to Company Owner.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Data / Backup / Purge dialog */}
      <Dialog open={!!dataTarget} onOpenChange={(o) => !o && setDataTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-slate-700" /> Data — {dataTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-5">
            {/* Backups */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Backups</Label>
                <Button size="sm" variant="outline" onClick={startBackup} disabled={backupBusy} data-testid="create-backup-btn">
                  {backupBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Database className="w-4 h-4 mr-1.5" />}
                  Create backup
                </Button>
              </div>
              {backups.length === 0 ? (
                <p className="text-sm text-slate-500">No backups yet.</p>
              ) : (
                <div className="space-y-2 max-h-44 overflow-y-auto">
                  {backups.map((b) => (
                    <div key={b.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid={`backup-row-${b.id}`}>
                      <div>
                        <div className="text-slate-700">{new Date(b.created_at).toLocaleString("en-AU")}</div>
                        <div className="text-xs text-slate-400">
                          {b.status === "ready" ? `${((b.size || 0) / 1024).toFixed(0)} KB · ${b.media_ok || 0} files` : b.status}
                        </div>
                      </div>
                      {b.status === "ready" ? (
                        <Button size="sm" variant="ghost" onClick={() => downloadBackup(b)} data-testid={`download-backup-${b.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : b.status === "running" ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      ) : <span className="text-xs text-red-500">failed</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Danger zone — Purge */}
            <div className="border border-red-200 bg-red-50/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm mb-1"><ShieldAlert className="w-4 h-4" /> Danger zone — Purge</div>
              <p className="text-xs text-slate-600 mb-2">Permanently deletes ALL of this company's data (users, sites, diaries, defects, photos, etc.). A full backup is taken automatically right before deletion and emailed to you as a safety net. Type the company name to confirm.</p>
              <Input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} placeholder={dataTarget?.name} className="mb-2 border-red-200" data-testid="purge-confirm-input" />
              <Button className="bg-red-600 hover:bg-red-700 text-white w-full" disabled={busy || purgeConfirm !== dataTarget?.name} onClick={doPurge} data-testid="confirm-purge-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Purge company permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
